import { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { invalidateScopes } from './queries';

const mq = (q: string) => (typeof window === 'undefined' ? null : window.matchMedia(q));

export function useMedia(query: string): boolean {
  return useSyncExternalStore(
    (cb) => {
      const m = mq(query);
      m?.addEventListener('change', cb);
      return () => m?.removeEventListener('change', cb);
    },
    () => mq(query)?.matches ?? false,
    () => false,
  );
}

/** screens.md breakpoints: mobile < 768px. */
export const useIsMobile = () => useMedia('(max-width: 767px)');

/** Live updates: refetch whatever the other member just changed (SSE, domain §6). */
export function useLiveUpdates(enabled: boolean) {
  useEffect(() => {
    if (!enabled || typeof EventSource === 'undefined') return;
    let es: EventSource | null = null;
    let retry: ReturnType<typeof setTimeout> | undefined;
    const open = () => {
      es = new EventSource('/api/events');
      es.addEventListener('change', (e) => {
        try {
          invalidateScopes(JSON.parse((e as MessageEvent).data).scopes ?? []);
        } catch {
          /* ignore malformed events */
        }
      });
      es.onerror = () => {
        es?.close();
        retry = setTimeout(open, 5000);
      };
    };
    open();
    return () => {
      clearTimeout(retry);
      es?.close();
    };
  }, [enabled]);
}

export function useOnline() {
  return useSyncExternalStore(
    (cb) => {
      window.addEventListener('online', cb);
      window.addEventListener('offline', cb);
      return () => {
        window.removeEventListener('online', cb);
        window.removeEventListener('offline', cb);
      };
    },
    () => navigator.onLine,
    () => true,
  );
}

export function useClickOutside<T extends HTMLElement>(onOutside: () => void, active = true) {
  const ref = useRef<T>(null);
  useEffect(() => {
    if (!active) return;
    const h = (e: PointerEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onOutside();
    };
    const t = setTimeout(() => document.addEventListener('pointerdown', h), 0);
    return () => {
      clearTimeout(t);
      document.removeEventListener('pointerdown', h);
    };
  }, [onOutside, active]);
  return ref;
}

export function useDebounced<T>(value: T, ms: number): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

export type ThemePref = 'system' | 'light' | 'dark';
export function applyTheme(pref: ThemePref) {
  localStorage.setItem('koverta.theme', pref);
  const t =
    pref === 'system' ? (matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light') : pref;
  document.documentElement.dataset.theme = t;
  document
    .querySelector('meta[name=theme-color]')
    ?.setAttribute('content', t === 'dark' ? '#131719' : '#1d4e6b');
}
export const themePref = (): ThemePref => (localStorage.getItem('koverta.theme') as ThemePref) || 'system';

const SIDE_KEY = 'koverta.sidebarWidth';
export const SIDE_DEFAULT = 248;
export const SIDE_MIN = 208;
const SIDE_MAX = 400;
/** The sidebar may never take more than this share of the viewport, so the page keeps room to breathe. */
const SIDE_MAX_SHARE = 0.32;

function readSideWidth(): number {
  const n = Number(localStorage.getItem(SIDE_KEY));
  return Number.isFinite(n) && n > 0 ? n : SIDE_DEFAULT;
}

/** User-resizable desktop sidebar width, persisted per browser and clamped to the current viewport. */
export function useSidebarWidth() {
  const [raw, setRaw] = useState(readSideWidth);
  const [vw, setVw] = useState(() => window.innerWidth);
  useEffect(() => {
    const on = () => setVw(window.innerWidth);
    window.addEventListener('resize', on);
    return () => window.removeEventListener('resize', on);
  }, []);
  const max = Math.max(SIDE_MIN, Math.min(SIDE_MAX, Math.floor(vw * SIDE_MAX_SHARE)));
  const clamp = (w: number) => Math.round(Math.min(max, Math.max(SIDE_MIN, w)));
  const setWidth = (w: number) => {
    const next = clamp(w);
    setRaw(next);
    localStorage.setItem(SIDE_KEY, String(next));
  };
  const reset = () => {
    setRaw(SIDE_DEFAULT);
    localStorage.removeItem(SIDE_KEY);
  };
  const width = clamp(raw);
  return { width, min: SIDE_MIN, max, setWidth, reset, content: vw - width };
}
